import {
  Badge,
  Button,
  Col,
  Divider,
  Popconfirm,
  Popover,
  QRCode,
  Row,
  Select,
  Space,
  Typography,
  message,
} from "antd"
import React, { useEffect, useState } from "react"
import {
  CheckCircleTwoTone,
  CloseCircleTwoTone,
  PrinterOutlined, // Changed from QrcodeOutlined
  TrademarkCircleOutlined,
} from "@ant-design/icons"
import { collection, deleteDoc, doc, onSnapshot } from "firebase/firestore"
import moment from "moment"
import { useCollection } from "../../../hooks/useCollection"
import { db } from "../../../firebase/config"
import PageHeader from "../../../components/Elements/PageHeader"
import StandartTable from "../../../components/Elements/StandartTable"
import CreateRoll from "./CreateRoll"
import EditRoll from "./EditRoll"
import { RollLocationDisplay } from "../../../components/Rolls/RollLocationDisplay"

const { Text, Link } = Typography

// Add your Cloud Function URL
const PRINT_API_URL = "https://us-central1-managers-testing.cloudfunctions.net/addPrintJob"

export default function WarehouseRolls() {
  const { data: materialData, loading: materialLoading } = useCollection("materials")
  const [rollsLoading, setRollsLoading] = useState(true)
  const [rollsData, setRollsData] = useState([])
  const [selectedMaterials, setSelectedMaterials] = useState([])
  const [selectedSlogans, setSelectedSlogans] = useState([])
  const [printing, setPrinting] = useState({}) // Track printing state per roll

  // Print label function
  const handlePrintLabel = async (roll) => {
    setPrinting((prev) => ({ ...prev, [roll.id]: true }))

    try {
      const response = await fetch(PRINT_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          barcode: roll.barcode || roll.id, // Use barcode if available, otherwise id
        }),
      })

      const result = await response.json()

      if (result.success) {
        message.success("Label sent to printer!")
      } else {
        message.error("Print failed: " + result.error)
      }
    } catch (error) {
      message.error("Failed to print: " + error.message)
    } finally {
      setPrinting((prev) => ({ ...prev, [roll.id]: false }))
    }
  }

  const handleMaterialSearch = (value) => {
    setSelectedMaterials(value)
  }

  const handleSloganSearch = (value) => {
    setSelectedSlogans(value)
  }

  const rollsColumns = [
    {
      title: "Location",
      dataIndex: "rollLocation",
      align: "center",
      render: (value) => <Badge status="processing" text={RollLocationDisplay(value)} />,
    },
    {
      title: "Material",
      dataIndex: ["material", "material"],
      align: "center",
    },
    {
      title: "Slogan",
      dataIndex: ["material", "slogan"],
      align: "center",
    },
    {
      title: "Print", // Changed from "QR"
      align: "center",
      render: (value, record) => (
        <Button
          type="link"
          icon={<PrinterOutlined />}
          loading={printing[record.id]}
          onClick={() => handlePrintLabel(record)}
          title="Print label"
        />
      ),
    },
    {
      title: "Meters",
      dataIndex: "remainingMeters",
      align: "center",
    },
    {
      title: "Width",
      dataIndex: "rollWidth",
      align: "center",
    },
    {
      title: "Barcode",
      dataIndex: "barcode",
      align: "center",
    },
    {
      title: "Status",
      dataIndex: "inStock",
      align: "center",
      render: (value) => {
        if (value === false) {
          return <CloseCircleTwoTone color="#1668dc" />
        } else {
          return <CheckCircleTwoTone color="#d32029" />
        }
      },
    },
    {
      title: "Last Used",
      dataIndex: "updated",
      align: "center",
      render: (value) => (
        <Text>{value?.seconds ? moment.unix(value.seconds).format("DD/MM/YYYY") : ""}</Text>
      ),
    },
    // {
    //   title: "Created",
    //   dataIndex: "created",
    //   align: "center",
    //   render: (value) => (
    //     <Text>{value?.seconds ? moment.unix(value.seconds).format("DD/MM/YYYY") : ""}</Text>
    //   ),
    // },
    {
      title: "Action",
      align: "center",
      render: (value) => (
        <Space split>
          <EditRoll
            materialLoading={materialLoading}
            materialData={materialData}
            rollProp={value}
          />
          <Popconfirm
            title="Are you sure？"
            placement="left"
            onConfirm={(event) => deleteRoll(event, value.id)}
            okText="Yes"
            cancelText="No">
            <Link style={{ color: "red" }}>delete</Link>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  const deleteRoll = async (e, docid) => {
    e.preventDefault()
    const ref = doc(db, "rolls", docid)
    try {
      await deleteDoc(ref).then(() => {
        message.success("Deleted!")
      })
    } catch (err) {
      message.success(err.message)
    }
  }

  useEffect(() => {
    setRollsLoading(true)
    const rollRef = collection(db, "rolls")

    const unsubscribe = onSnapshot(rollRef, (snapshot) => {
      let results = []
      snapshot.docs.forEach((doc) => {
        results.push({ ...doc.data(), id: doc.id })
      })
      let searchMaterial = [...selectedMaterials, ...selectedSlogans]
      if (searchMaterial && searchMaterial.length > 0) {
        results = results.filter((roll) => searchMaterial.includes(roll.material.id))
      }
      setRollsData(results)
      setRollsLoading(false)
    })
    return () => unsubscribe()
  }, [selectedMaterials, selectedSlogans])

  return (
    <div style={{ padding: 15 }}>
      <Row>
        <Col span={24}>
          <PageHeader text="Warehouse Rolls" icon={<TrademarkCircleOutlined />} />
        </Col>
        <Col span={24}>
          <Space split={<Divider type="vertical" />}>
            <CreateRoll materialData={materialData} materialLoading={materialLoading} />

            <Space>
              Filter By Material :
              <Select
                showSearch
                mode="multiple"
                allowClear
                style={{ width: 350 }}
                placeholder="Please select"
                onChange={handleMaterialSearch}
                fieldNames={{ label: "material", value: "id" }}
                optionFilterProp="children"
                filterOption={(input, option) => (option?.material ?? "").includes(input)}
                filterSort={(optionA, optionB) =>
                  (optionA?.material ?? "")
                    .toLowerCase()
                    .localeCompare((optionB?.material ?? "").toLowerCase())
                }
                options={materialData}
              />
            </Space>
            <Space>
              Slogan :
              <Select
                showSearch
                mode="multiple"
                allowClear
                style={{ width: 350 }}
                placeholder="Please select"
                onChange={handleSloganSearch}
                fieldNames={{ label: "slogan", value: "id" }}
                optionFilterProp="children"
                filterOption={(input, option) => (option?.slogan ?? "").includes(input)}
                filterSort={(optionA, optionB) =>
                  (optionA?.slogan ?? "")
                    .toLowerCase()
                    .localeCompare((optionB?.slogan ?? "").toLowerCase())
                }
                options={materialData}
              />
            </Space>
          </Space>
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          <StandartTable data={rollsData} loading={rollsLoading} columns={rollsColumns} />
        </Col>
      </Row>
    </div>
  )
}
